/**
 * Shared helpers for pattern renderers.
 *
 * Idiom every pattern renderer follows:
 *  - root element: `<section id="{anchor}" class="ve-section ve-{type}[ ve-{type}--{variant}]">`
 *  - ALL interpolated text goes through escapeHtml, ALL urls through safeUrl
 *  - any spot whose fields-path appears in the editable manifest gets
 *    ` data-edit="{fieldId}"` via editAttr(section, '/path')
 *  - fields are read defensively (str/list helpers) — a missing optional
 *    field renders nothing, never "undefined"
 */
import { escapeHtml } from '../escape.js';
import type { JsonValueT, PatternSectionT } from '../../schema/index.js';

/** Qualified editable identity when the manifest addresses this fields-path, else ''. */
export function editAttr(section: PatternSectionT, path: string): string {
  const entry = section.editableManifest.find((candidate) => candidate.path === path);
  if (!entry) return '';
  const fieldId = escapeHtml(entry.fieldId);
  return ` data-edit="${fieldId}" data-veldon-section="${escapeHtml(section.id)}" data-veldon-field="${fieldId}"`;
}

/** Read a string field; '' when absent or not a string. */
export function str(fields: Record<string, JsonValueT>, key: string): string {
  const value = fields[key];
  return typeof value === 'string' ? value : '';
}

/** Read an array field; [] when absent or not an array. */
export function list(fields: Record<string, JsonValueT>, key: string): JsonValueT[] {
  const value = fields[key];
  return Array.isArray(value) ? value : [];
}

/** Read a string property off a JSON object item; '' otherwise. */
export function itemStr(item: JsonValueT, key: string): string {
  if (item === null || typeof item !== 'object' || Array.isArray(item)) return '';
  const value = (item as { [key: string]: JsonValueT })[key];
  return typeof value === 'string' ? value : '';
}

/** Root section opening tag with canonical classes and the anchor id.
 *  `variant` is escaped: the legacy v1 schema accepts any string, so an
 *  unescaped value could break out of the class attribute. */
export function sectionOpen(section: PatternSectionT, extraClass = ''): string {
  const variantClass = section.variant ? ` ve-${section.type}--${escapeHtml(section.variant)}` : '';
  const extra = extraClass ? ` ${extraClass}` : '';
  return `<section id="${escapeHtml(section.anchor)}" class="ve-section ve-${section.type}${variantClass}${extra}">`;
}

/** Escaped editable text element, omitted when its text is empty. */
export function headingBlock(
  section: PatternSectionT,
  tag: 'h1' | 'h2' | 'h3' | 'p',
  className: string,
  path: string,
  text: string,
  indent = '  ',
): string {
  if (!text) return '';
  return `${indent}<${tag} class="${className}"${editAttr(section, path)}>${escapeHtml(text)}</${tag}>`;
}

/** Standard optional label + heading pair used by section renderers. */
export function labeledIntro(
  section: PatternSectionT,
  classBase: string,
  heading: string,
  label = '',
  headingTag: 'h1' | 'h2' = 'h2',
  indent = '  ',
): string[] {
  return [
    headingBlock(section, 'p', `ve-${classBase}__label`, '/label', label, indent),
    headingBlock(section, headingTag, `ve-${classBase}__heading`, '/heading', heading, indent),
  ].filter(Boolean);
}

/** Editable collection wrapper with optional pre-rendered items. */
export function itemCollection(
  section: PatternSectionT,
  tag: 'div' | 'dl' | 'ol',
  className: string,
  path: string,
  items: string,
  indent = '  ',
): string {
  const lines = [`${indent}<${tag} class="${className}"${editAttr(section, path)}>`];
  if (items) lines.push(items);
  lines.push(`${indent}</${tag}>`);
  return lines.join('\n');
}
